"use client";

import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Pin, MessageSquare } from "lucide-react";
import { useChatStore, getTenantGreeting, type Conversation } from "@/lib/chat-store";
import { cn } from "@/lib/utils";
import { useTenant } from "@/components/TenantProvider";

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatRelativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return "Baru saja";
  if (minutes < 60) return `${minutes} mnt`;
  if (hours < 24) return `${hours} jam`;
  if (days < 7) return `${days} hari`;
  const d = new Date(ts);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear().toString().slice(-2)}`;
}

function ConversationItem({
  conversation,
  isActive,
  onClick,
}: {
  conversation: Conversation;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <motion.button
      layout
      initial={{ opacity: 0, x: -16 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -16 }}
      transition={{ duration: 0.2 }}
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 px-4 py-3 text-left relative",
        "transition-colors duration-150",
        "border-l-[3px]",
        isActive
          ? "border-l-gold bg-gold/[0.06]"
          : "border-l-transparent hover:bg-white/[0.03] dark:hover:bg-white/[0.03] hover:bg-black/[0.02]",
      )}
    >
      {/* Avatar */}
      <div className="relative shrink-0">
        <div
          className={cn(
            "flex items-center justify-center w-11 h-11 rounded-full",
            "bg-gold/20 text-gold font-semibold text-sm",
            "border border-gold/30",
          )}
        >
          {getInitials(conversation.customerName)}
        </div>

        {/* Online indicator */}
        {conversation.isOnline && (
          <span
            className={cn(
              "absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full",
              "bg-green-500 border-[2px] border-dark-bg dark:border-dark-bg border-light-bg",
            )}
          />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            {conversation.pinned && (
              <Pin className="w-3 h-3 shrink-0 text-gold/60" />
            )}
            <span
              className={cn(
                "text-sm font-semibold truncate",
                "text-dark-text-primary dark:text-dark-text-primary text-light-text-primary",
              )}
            >
              {conversation.customerName}
            </span>
          </div>
          <span
            className={cn(
              "shrink-0 text-[11px]",
              "text-dark-text-secondary/60 dark:text-dark-text-secondary/60 text-light-text-secondary/60",
            )}
          >
            {formatRelativeTime(conversation.lastMessageTime)}
          </span>
        </div>

        <div className="flex items-center justify-between gap-2 mt-0.5">
          {conversation.isTyping ? (
            <span className="text-xs text-gold italic">Mengetik...</span>
          ) : (
            <p
              className={cn(
                "text-xs truncate",
                "text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary",
              )}
            >
              {conversation.lastMessage}
            </p>
          )}

          {conversation.unreadCount > 0 && (
            <span
              className={cn(
                "shrink-0 flex items-center justify-center min-w-[18px] h-[18px] px-1.5",
                "rounded-full bg-gold",
                "text-[10px] font-bold text-dark-bg dark:text-dark-bg",
              )}
            >
              {conversation.unreadCount > 9 ? "9+" : conversation.unreadCount}
            </span>
          )}
        </div>

        {/* Booking indicator */}
        {conversation.bookingId && (
          <p className="mt-1 text-[10px] text-gold/70 font-medium">
            {conversation.bookingId}
          </p>
        )}
      </div>
    </motion.button>
  );
}

export function ConversationList() {
  const { getMyConversations, activeConversationId, setActiveConversation } =
    useChatStore();
  const { tenant } = useTenant();
  const [search, setSearch] = useState("");

  const myConversations = getMyConversations();

  // Sort: pinned first, then by lastMessageTime desc
  const sortedConversations = useMemo(() => {
    let list = [...myConversations];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((c) => c.customerName.toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return b.lastMessageTime - a.lastMessageTime;
    });
    return list;
  }, [myConversations, search]);

  return (
    <div className="flex flex-col h-full">
      {/* Search bar */}
      <div className="px-4 pt-3 pb-2">
        <div
          className={cn(
            "flex items-center gap-2 px-3 py-2 rounded-[14px]",
            "bg-white/[0.04] dark:bg-white/[0.04] bg-black/[0.03]",
            "border border-white/[0.05] dark:border-white/[0.05] border-black/[0.05]",
            "focus-within:border-gold/30 dark:focus-within:border-gold/30",
            "transition-colors duration-200",
          )}
        >
          <Search className="w-4 h-4 shrink-0 text-dark-text-secondary/50 dark:text-dark-text-secondary/50 text-light-text-secondary/50" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari percakapan..."
            className={cn(
              "flex-1 bg-transparent text-sm outline-none",
              "text-dark-text-primary dark:text-dark-text-primary text-light-text-primary",
              "placeholder:text-dark-text-secondary/40 dark:placeholder:text-dark-text-secondary/40 placeholder:text-light-text-secondary/40",
            )}
          />
        </div>
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto scrollbar-none">
        {sortedConversations.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center h-full px-6 py-12 text-center"
          >
            <div
              className={cn(
                "w-16 h-16 rounded-full flex items-center justify-center mb-4",
                "bg-gold/10",
              )}
            >
              <MessageSquare className="w-7 h-7 text-gold/50" />
            </div>
            <p
              className={cn(
                "text-sm font-medium mb-1",
                "text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary",
              )}
            >
              {search.trim() ? "Tidak ada hasil" : "Belum ada percakapan"}
            </p>
            <p
              className={cn(
                "text-xs",
                "text-dark-text-secondary/60 dark:text-dark-text-secondary/60 text-light-text-secondary/60",
              )}
            >
              {search.trim()
                ? "Coba kata kunci lain"
                : "Percakapan kamu dengan tim studio akan muncul di sini"}
            </p>
            {!search.trim() && (
              <button
                onClick={() => {
                  const { addConversation, setActiveConversation, currentUserId, currentTenantId } = useChatStore.getState();
                  const tid = currentTenantId || tenant.id;
                  const greeting = getTenantGreeting(tid);
                  const id = `conv-${tid}-${Date.now()}`;
                  const conv: Conversation = {
                    id,
                    customerId: currentUserId,
                    customerName: "Admin Support",
                    tenantId: tid,
                    lastMessage: greeting.split("\n")[0] || "Mulai percakapan baru",
                    lastMessageTime: Date.now(),
                    unreadCount: 0,
                    isOnline: true,
                    isTyping: false,
                    aiEnabled: true,
                    messages: [{
                      id: `msg-${Date.now()}`,
                      conversationId: id,
                      senderId: "ai-agent",
                      senderName: "AI Assistant",
                      senderRole: "ai",
                      text: greeting,
                      timestamp: Date.now(),
                      status: "sent",
                    }],
                  };
                  addConversation(conv);
                  setActiveConversation(id);
                }}
                className="mt-4 px-4 py-2 rounded-full bg-gold text-white text-xs font-medium hover:bg-gold-light transition-colors"
              >
                💬 Mulai Chat
              </button>
            )}
          </motion.div>
        ) : (
          <AnimatePresence mode="popLayout">
            <div className="pb-2">
              {sortedConversations.map((conv) => (
                <ConversationItem
                  key={conv.id}
                  conversation={conv}
                  isActive={conv.id === activeConversationId}
                  onClick={() => setActiveConversation(conv.id)}
                />
              ))}
            </div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
